import { Button, Stack } from '@mui/material';
import { addHours, addMonths, format, parse, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { produce } from 'immer';
import { Map } from 'immutable';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

// eslint-disable-next-line no-restricted-imports -- pre-existing EnhancedTable usage; migrate off when touched
import EnhancedTable from '@sophocles/components/EnhancedTable';
import { fetch2PDCProofOfPerformanceReports } from '@sophocles/entities/ProofOfPerformance2PDCReport';
import { fetch2PDCProofOfPerformanceRollupReports } from '@sophocles/entities/ProofOfPerformance2PDCReport/actions';
import { generateQueryParams, getExcelFile } from '@sophocles/utils';

function timeToDateTime(s) {
  const sd = s.split(':');
  return new Date(2000, 0, 1, parseInt(sd[0]), parseInt(sd[1]), 0, 0);
}

function utcToLocal(targetDate, targetTime, timeZone, formatStr) {
  const u = toZonedTime(parseISO(`${targetDate}T${targetTime ?? '00:00:00'}Z`), timeZone);
  return format(u, formatStr);
}

// site/siteGroup default to an empty Map for the plant-level rollup route
// (ProofOfPerformanceView), which has no single site in scope.
function EnhancedPOPTable({ customerId, siteId, siteGroupId, site = Map(), siteGroup = Map(), ...tableProps }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const table = useRef();

  const [startDate, setStartDate] = useState(addMonths(new Date(), -1));
  const [endDate, setEndDate] = useState(new Date());

  const isRollup = !siteId && siteGroupId != null;

  const getDataFetcher = () => params =>
    isRollup
      ? dispatch(fetch2PDCProofOfPerformanceRollupReports(siteGroupId, generateQueryParams({ ...params, limit: 100 })))
      : dispatch(fetch2PDCProofOfPerformanceReports(siteId, generateQueryParams(params)));

  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const exportExcel = sortedList => {
    const title = 'pop-report-' + Date.now();
    const headers = [];
    const columns = [
      'report_date',
      'relay_number',
      'schedule_number',
      'on_time',
      'off_time',
      'input_voltage',
      'duty_cycle',
      'watt_hours',
      'lvt_time',
      'hvt_time',
      'alarmText',
      'passFail',
    ];

    const modified = produce(sortedList, draft => {
      draft.forEach((v, i) => {
        draft[i].alarmText = v.alarms != null ? v.alarms.join(',') : '';
      });
      return draft;
    });

    headers.push([t('pop.reportTitle')]);
    headers.push([t('pop.plantLabel') + ' ' + siteGroup.get('name', t('pop.noPlant'))]);
    headers.push([t('pop.siteLabel') + ' ' + site.get('name', t('pop.noSite'))]);
    headers.push([]);
    headers.push([
      t('pop.reportDate'),
      t('pop.relayNumber'),
      t('pop.scheduleNumber'),
      t('pop.onTime'),
      t('pop.offTime'),
      t('pop.voltage'),
      t('pop.brightness'),
      t('pop.wattHours'),
      t('pop.lowVoltage'),
      t('pop.highVoltage'),
      t('pop.alarms'),
      t('general.status'),
    ]);

    getExcelFile(modified, columns, headers, title);
  };

  const reportColumns = [
    {
      id: 'report_date',
      label: t('general.date'),
      style: { minWidth: '120px' },
    },
    {
      id: 'relay_number',
      label: t('pop.relay'),
      style: { minWidth: '50px' },
    },
    {
      id: 'schedule_number',
      label: t('pop.schedule'),
      style: { minWidth: '50px' },
    },
    {
      id: 'on_time',
      label: t('pop.onOff'),
      style: { minWidth: '150px' },
      cellRenderer: (datum, row, rowIndex) => {
        const onTime = utcToLocal(row.report_date, datum, site.get('time_zone', 'US/Eastern'), 'HH:mm'); // moment(datum, 'HH:mm:ss').format('HH:mm');

        let offTime = utcToLocal(row.report_date, row.off_time, site.get('time_zone', 'US/Eastern'), 'HH:mm'); //_.get(row, 'off_time', null);

        let lvtTime = null;

        if (row.lvt_time != null) {
          lvtTime = utcToLocal(row.report_date, row.lvt_time, site.get('time_zone', 'US/Eastern'), 'HH:mm'); //_.get(row, 'lvt_time', null);
          if (lvtTime != null) {
            offTime = lvtTime;
          }
        }

        let onDuration = _.get(row, 'on_duration', null);
        if (onDuration == null || lvtTime != null) {
          const onDateTime = timeToDateTime(format(parse(onTime, 'HH:mm', new Date()), 'hh:mm'));
          const offDateTime = timeToDateTime(format(parse(offTime, 'HH:mm', new Date()), 'hh:mm'));

          if (offDateTime < onDateTime) {
            offDateTime.setDate(offDateTime.getDate() + 1);
          }

          const msec = offDateTime - onDateTime;
          onDuration = Math.floor((msec / 1000 / 60) << 0);
        }

        return (
          <div>
            <div>{`${onTime} - ${offTime}`}</div>
            <small>{t('pop.minutes', { count: onDuration })}</small>
          </div>
        );
      },
    },
    {
      id: 'input_voltage',
      label: t('pop.voltage'),
      style: { minWidth: '50px' },
      cellRenderer: (datum, row, rowIndex) => `${datum ? datum.toFixed(1) : '0.0'} V`,
    },
    {
      id: 'duty_cycle',
      label: t('pop.brightness'),
      style: { minWidth: '50px' },
      cellRenderer: (datum, row, rowIndex) => `${datum ? datum.toFixed(0) : '0'}%`,
    },
    {
      id: 'watt_hours',
      label: t('pop.wattHours'),
      style: { minWidth: '70px' },
      cellRenderer: (datum, row, rowIndex) => `${datum ? datum.toFixed(2) : '0.00'} Wh`,
    },
    {
      id: 'lvt_time',
      label: t('pop.thresholdLow'),
      style: { minWidth: '70px' },
      cellRenderer: (datum, row, rowIndex) => {
        // 'HH:mm:ss.SSS'
        const lvtTime = utcToLocal(row.report_date, row.lvt_time, site.get('time_zone', 'US/Eastern'), 'HH:mm'); //_.get(row, 'lvt_time', null);
        return (
          <div>
            <div>{datum === '' ? '-' : lvtTime}</div>
            <small>{`${_.get(row, 'low_voltage_threshold', '-')} V`}</small>
          </div>
        );
      },
    },
    {
      id: 'hvt_time',
      label: t('pop.thresholdHigh'),
      style: { minWidth: '70px' },
      cellRenderer: (datum, row, rowIndex) => (
        <div>
          <div>{datum === '' ? '-' : datum}</div>
          <small>{`${_.get(row, 'high_voltage_threshold', '-')} V`}</small>
        </div>
      ),
    },
    {
      id: 'alarms',
      label: t('pop.alarms'),
      style: { minWidth: '120px' },
      cellRenderer: (datum, row, rowIndex) => {
        return datum.length == 0 ? (
          <small key={`alarm-none-${rowIndex}`}>-</small>
        ) : (
          <Stack key={`alarm-${rowIndex}`} direction="row" spacing={1}>
            {datum.map(a => {
              if (a.display_name != null) {
                return <small key={`a-${a.id}`}>{a.display_name}</small>;
              }
              return <small key={`a-${a.id}`}>{t('pop.unknown')}</small>;
            })}
          </Stack>
        );
      },
    },
    {
      id: 'passed',
      label: t('general.status'),
      style: { minWidth: '70px' },
      cellRenderer: (datum, row, rowIndex) =>
        datum ? (
          <span style={{ fontWeight: 'bold', color: 'green' }}>{t('pop.pass')}</span>
        ) : (
          <span style={{ fontWeight: 'bold', color: 'red' }}>{t('pop.fail')}</span>
        ),
    },
  ];

  const groupedColumns = [
    {
      id: 'site',
      label: t('pop.id'),
      param: 'site__id',
      style: { minWidth: '120px' },
      cellRenderer: (datum, row, rowIndex) => (
        <Link
          style={{ fontWeight: 'bold' }}
          to={`/c/${customerId}/plant/${siteGroupId}/structure/${_.get(datum, 'id', null)}/pop`}
        >
          {_.get(datum, 'id', null)}
        </Link>
      ),
    },
    {
      id: 'site',
      label: t('general.name'),
      param: 'site__name',
      style: { minWidth: '120px' },
      cellRenderer: (datum, row, rowIndex) => _.get(datum, 'name', '-'),
    },
    {
      id: 'site',
      label: t('general.description'),
      param: 'site__description',
      style: { minWidth: '120px' },
      cellRenderer: (datum, row, rowIndex) => _.get(datum, 'description', '-'),
    },
  ];

  const filters = [];

  return (
    <EnhancedTable
      dateRange
      ref={table}
      title={t('pop.proofOfPerformance')}
      columns={reportColumns}
      filters={filters}
      startDate={startDate}
      endDate={endDate}
      dateKeyword="report_date"
      onDateRangeChange={handleDateRangeChange}
      fetchData={getDataFetcher()}
      grouping={isRollup ? 'site.id' : null}
      groupedColumns={groupedColumns}
      performGrouping={(grouping, groupedData) => {
        const data = {};
        const groupingPath = grouping.split('.');
        groupedData.forEach(datum => {
          const groupingValue = _.get(datum, groupingPath, null);
          if (groupingValue in data) {
            const groupingData = data[groupingValue].data;
            groupingData.push(datum);
            data[groupingValue].data = groupingData;
          } else {
            data[groupingValue] = {
              groupingValue,
              site: _.get(datum, 'site', null),
              data: [datum],
            };
          }
        });
        return Object.values(data);
      }}
      actions={
        isRollup
          ? null
          : [
              <Button
                key="export"
                size="small"
                variant="contained"
                startIcon={<i className="far fa-file-excel" />}
                onClick={() => {
                  exportExcel(table.current.getData());
                }}
                sx={{ backgroundColor: '#249138' }}
              >
                {t('pop.excel')}
              </Button>,
            ]
      }
      defaultRowsPerPage={25}
      defaultOrderBy="report_date"
      defaultOrder="desc"
      {...tableProps}
    />
  );
}

export default EnhancedPOPTable;
